import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['card', 'column', 'searchInput', 'searchClear', 'searchResults']
  static values = {
    csrfToken: String
  }

  connect () {
    this.draggedCard = null
    this.csrfTokenValue = document.querySelector('meta[name="csrf-token"]')?.content
    this.highlightTimers = new Map() // Хранилище таймеров для очистки
  }

  disconnect () {
    // Очищаем все таймеры при отключении контроллера
    this.highlightTimers.forEach(timerId => clearTimeout(timerId))
    this.highlightTimers.clear()
  }

  // Начало перетаскивания
  dragStart (event) {
    this.draggedCard = event.target.closest('[data-kanban-target="card"]')
    if (!this.draggedCard) return

    this.draggedCard.classList.add('kanban-card--dragging')
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', this.draggedCard.dataset.reservationId)
  }

  // Конец перетаскивания
  dragEnd (event) {
    if (this.draggedCard) {
      this.draggedCard.classList.remove('kanban-card--dragging')
    }
    this.draggedCard = null

    // Убираем подсветку со всех колонок
    this.columnTargets.forEach(column => {
      column.classList.remove('kanban-column--drag-over')
    })
  }

  // При перетаскивании над колонкой
  dragOver (event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'

    const column = event.target.closest('[data-kanban-target="column"]')
    if (column) {
      column.classList.add('kanban-column--drag-over')
    }
  }

  // При выходе из колонки
  dragLeave (event) {
    const column = event.target.closest('[data-kanban-target="column"]')
    if (column && !column.contains(event.relatedTarget)) {
      column.classList.remove('kanban-column--drag-over')
    }
  }

  // Сброс карточки в колонку
  async drop (event) {
    event.preventDefault()

    const column = event.target.closest('[data-kanban-target="column"]')
    if (!column || !this.draggedCard) return

    column.classList.remove('kanban-column--drag-over')

    const newStatus = column.dataset.status
    const currentStatus = this.draggedCard.dataset.status
    const reservationId = this.draggedCard.dataset.reservationId

    // Если статус не изменился — ничего не делаем
    if (newStatus === currentStatus) return

    // Сохраняем состояние для возможного отката
    const card = this.draggedCard
    const originalColumn = this.columnTargets.find(col => col.dataset.status === currentStatus)
    const originalCardsList = originalColumn?.querySelector('.kanban-column__cards')

    // Перемещаем карточку визуально в НАЧАЛО списка
    const cardsList = column.querySelector('.kanban-column__cards')
    const emptyPlaceholder = cardsList.querySelector('.kanban-column__empty')
    if (emptyPlaceholder) {
      emptyPlaceholder.remove()
    }
    cardsList.prepend(card)

    // Обновляем data-атрибут
    card.dataset.status = newStatus

    // Обновляем бейдж статуса на карточке
    this.updateCardBadge(card, newStatus)

    // Подсвечиваем карточку как недавно перемещённую
    this.highlightCard(card)

    // Обновляем счётчики
    this.updateColumnCounts()

    // Проверяем пустые колонки
    this.checkEmptyColumns()

    // Отправляем запрос на сервер
    const success = await this.updateStatus(reservationId, newStatus)

    // При ошибке — откатываем изменения
    if (!success) {
      this.rollbackCard(card, currentStatus, originalCardsList)
    }
  }

  // Смена статуса через кнопку
  async changeStatus (event) {
    event.preventDefault()

    const button = event.currentTarget
    const card = button.closest('[data-kanban-target="card"]')
    const newStatus = button.dataset.status
    const reservationId = card.dataset.reservationId
    const currentStatus = card.dataset.status

    if (newStatus === currentStatus) return

    // Находим целевую колонку
    const targetColumn = this.columnTargets.find(col => col.dataset.status === newStatus)
    if (!targetColumn) return

    // Сохраняем состояние для возможного отката
    const originalColumn = this.columnTargets.find(col => col.dataset.status === currentStatus)
    const originalCardsList = originalColumn?.querySelector('.kanban-column__cards')

    // Перемещаем карточку в НАЧАЛО списка
    const cardsList = targetColumn.querySelector('.kanban-column__cards')
    const emptyPlaceholder = cardsList.querySelector('.kanban-column__empty')
    if (emptyPlaceholder) {
      emptyPlaceholder.remove()
    }
    cardsList.prepend(card)
    card.dataset.status = newStatus

    // Обновляем бейдж
    this.updateCardBadge(card, newStatus)

    // Подсвечиваем карточку как недавно перемещённую
    this.highlightCard(card)

    // Обновляем счётчики
    this.updateColumnCounts()

    // Проверяем пустые колонки
    this.checkEmptyColumns()

    // Отправляем запрос
    const success = await this.updateStatus(reservationId, newStatus)

    // При ошибке — откатываем изменения
    if (!success) {
      this.rollbackCard(card, currentStatus, originalCardsList)
    }
  }

  // AJAX запрос на обновление статуса
  async updateStatus (reservationId, newStatus) {
    try {
      const response = await fetch(`/admin/reservations/${reservationId}/update_status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue,
          Accept: 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      })

      if (!response.ok) {
        const data = await response.json()
        console.error('Ошибка обновления статуса:', data.errors)
        return false
      }
      return true
    } catch (error) {
      console.error('Ошибка сети:', error)
      return false
    }
  }

  // Откат карточки при ошибке
  rollbackCard (card, originalStatus, originalCardsList) {
    if (!originalCardsList) return

    // Возвращаем карточку в исходную колонку
    originalCardsList.prepend(card)
    card.dataset.status = originalStatus

    // Обновляем бейдж и кнопки
    this.updateCardBadge(card, originalStatus)

    // Показываем ошибку на карточке
    card.classList.remove('kanban-card--just-moved')
    card.classList.add('kanban-card--error')
    setTimeout(() => card.classList.remove('kanban-card--error'), 3000)

    // Обновляем счётчики и пустые колонки
    this.updateColumnCounts()
    this.checkEmptyColumns()
  }

  // Обновление бейджа статуса на карточке
  updateCardBadge (card, status) {
    const badge = card.querySelector('.kanban-card__status')
    if (!badge) return

    // Убираем все классы статусов
    badge.classList.remove(
      'admin-status-badge--success',
      'admin-status-badge--info',
      'admin-status-badge--error',
      'admin-status-badge--default'
    )

    // Добавляем нужный класс и текст
    const statusConfig = {
      active: { class: 'admin-status-badge--success', text: 'Активна' },
      in_progress: { class: 'admin-status-badge--info', text: 'В работе' },
      completed: { class: 'admin-status-badge--default', text: 'Завершена' },
      cancelled: { class: 'admin-status-badge--error', text: 'Отменена' }
    }

    const config = statusConfig[status]
    if (config) {
      badge.classList.add(config.class)
      badge.textContent = config.text
    }

    // Обновляем кнопки действий
    this.updateCardActions(card, status)
  }

  // Обновление кнопок действий на карточке
  updateCardActions (card, status) {
    const actionsContainer = card.querySelector('.kanban-card__actions')
    if (!actionsContainer) return

    // Сохраняем ссылку "Подробнее"
    const detailsLink = actionsContainer.querySelector('.kanban-card__action-btn--link')
    const detailsHref = detailsLink ? detailsLink.getAttribute('href') : null

    // Генерируем новые кнопки в зависимости от статуса
    let buttonsHtml = ''

    switch (status) {
      case 'active':
        buttonsHtml = `
          <button type="button" class="kanban-card__action-btn kanban-card__action-btn--primary"
                  data-action="click->kanban#changeStatus"
                  data-status="in_progress">
            В работу
          </button>
          <button type="button" class="kanban-card__action-btn kanban-card__action-btn--danger"
                  data-action="click->kanban#changeStatus"
                  data-status="cancelled">
            Отменить
          </button>
        `
        break
      case 'in_progress':
        buttonsHtml = `
          <button type="button" class="kanban-card__action-btn kanban-card__action-btn--success"
                  data-action="click->kanban#changeStatus"
                  data-status="completed">
            Завершить
          </button>
          <button type="button" class="kanban-card__action-btn kanban-card__action-btn--secondary"
                  data-action="click->kanban#changeStatus"
                  data-status="active">
            Вернуть
          </button>
        `
        break
      case 'completed':
      case 'cancelled':
        buttonsHtml = `
          <button type="button" class="kanban-card__action-btn kanban-card__action-btn--secondary"
                  data-action="click->kanban#changeStatus"
                  data-status="active">
            Восстановить
          </button>
        `
        break
    }

    // Добавляем ссылку "Подробнее"
    if (detailsHref) {
      buttonsHtml += `
        <a href="${detailsHref}" class="kanban-card__action-btn kanban-card__action-btn--link">
          Подробнее
        </a>
      `
    }

    actionsContainer.innerHTML = buttonsHtml
  }

  // Обновление счётчиков в заголовках колонок
  updateColumnCounts () {
    this.columnTargets.forEach(column => {
      const count = column.querySelectorAll('[data-kanban-target="card"]').length
      const countEl = column.querySelector('.kanban-column__count')
      if (countEl) {
        countEl.textContent = count
      }
    })
  }

  // Подсветка недавно перемещённой карточки
  highlightCard (card) {
    const cardId = card.dataset.reservationId

    // Очищаем предыдущий таймер для этой карточки
    if (this.highlightTimers.has(cardId)) {
      clearTimeout(this.highlightTimers.get(cardId))
    }

    // Убираем подсветку с других карточек
    this.cardTargets.forEach(c => c.classList.remove('kanban-card--just-moved'))

    // Добавляем подсветку
    card.classList.add('kanban-card--just-moved')

    // Добавляем метку времени перемещения
    let timeLabel = card.querySelector('.kanban-card__moved-label')
    if (!timeLabel) {
      timeLabel = document.createElement('div')
      timeLabel.className = 'kanban-card__moved-label'
      card.querySelector('.kanban-card__header').appendChild(timeLabel)
    }
    timeLabel.textContent = 'Только что'

    // Убираем подсветку через 30 секунд
    const timerId = setTimeout(() => {
      card.classList.remove('kanban-card--just-moved')
      if (timeLabel) {
        timeLabel.textContent = ''
      }
      this.highlightTimers.delete(cardId)
    }, 30000)

    this.highlightTimers.set(cardId, timerId)
  }

  // Проверка пустых колонок и добавление плейсхолдера
  checkEmptyColumns () {
    this.columnTargets.forEach(column => {
      const cardsList = column.querySelector('.kanban-column__cards')
      const cards = cardsList.querySelectorAll('[data-kanban-target="card"]:not([style*="display: none"])')
      const emptyPlaceholder = cardsList.querySelector('.kanban-column__empty')

      if (cards.length === 0 && !emptyPlaceholder) {
        const placeholder = document.createElement('div')
        placeholder.className = 'kanban-column__empty'
        placeholder.innerHTML = '<p>Нет бронирований</p>'
        cardsList.appendChild(placeholder)
      } else if (cards.length > 0 && emptyPlaceholder) {
        emptyPlaceholder.remove()
      }
    })
  }

  // Фильтрация карточек по поисковому запросу
  filterCards () {
    const query = this.searchInputTarget.value.toLowerCase().trim()

    // Показываем/скрываем кнопку очистки
    if (this.hasSearchClearTarget) {
      this.searchClearTarget.style.display = query ? 'flex' : 'none'
    }

    let visibleCount = 0
    const totalCount = this.cardTargets.length

    this.cardTargets.forEach(card => {
      if (!query) {
        card.style.display = ''
        visibleCount++
        return
      }

      const code = card.dataset.code || ''
      const customerName = card.dataset.customerName || ''
      const customerPhone = card.dataset.customerPhone || ''

      const matches = code.includes(query) ||
                      customerName.includes(query) ||
                      customerPhone.includes(query)

      card.style.display = matches ? '' : 'none'
      if (matches) visibleCount++
    })

    // Показываем результаты поиска
    if (this.hasSearchResultsTarget) {
      if (query) {
        this.searchResultsTarget.textContent = `Найдено: ${visibleCount} из ${totalCount}`
        this.searchResultsTarget.style.display = 'block'
      } else {
        this.searchResultsTarget.style.display = 'none'
      }
    }

    // Обновляем счётчики и пустые колонки
    this.updateVisibleColumnCounts()
    this.checkEmptyColumns()
  }

  // Очистка поиска
  clearSearch () {
    this.searchInputTarget.value = ''
    this.filterCards()
    this.searchInputTarget.focus()
  }

  // Обновление счётчиков видимых карточек
  updateVisibleColumnCounts () {
    this.columnTargets.forEach(column => {
      const visibleCards = column.querySelectorAll('[data-kanban-target="card"]:not([style*="display: none"])')
      const countEl = column.querySelector('.kanban-column__count')
      if (countEl) {
        countEl.textContent = visibleCards.length
      }
    })
  }
}
